var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useMemo, useState } from 'react';
import { ArticleCardEnergyLabel, ArticleCardOverlay, ArticleCardVariant } from '@lux/article-card';
import { BrandColor, Preset, Tag, Text } from '@lux/text';
import { Flag, FlagType } from '@lux/flags';
import { Translate, TranslateScope } from '@pegasus/translation';
import { trackSelectContent } from '@browsing/tracking';
import { FormatPrice } from '../../format-price';
import { BaseArticleCard } from '../base';
import { StockStatus } from '../types';
import { EnergyDialog, getEnergyIcon } from '../../energy-info';
import { ReservedPopover } from '../../reserved-popover';
import * as S from './styles';
import { PlusEarlyAccessBadge, PlusExclusiveBadge } from '../../plus-elements';
import { useEnergyLabel, usePlusEarlyAccessElements, usePlusExclusiveElements, useSustainabilityFlag, } from './hooks';
var EnergyLabel = function (_a) {
    var energyLabelInfo = _a.energyLabelInfo, sku = _a.sku, energyEfficiencyClass = _a.energyEfficiencyClass;
    var isEnergyLabelVisible = useEnergyLabel();
    if (!isEnergyLabelVisible)
        return null;
    var energyIcon = getEnergyIcon(energyEfficiencyClass, false);
    var handleOnOpen = function () {
        trackSelectContent('energy_modal', 'click', {
            contentId: sku,
            info: "energy_score_".concat(energyEfficiencyClass),
        });
    };
    return (React.createElement(TranslateScope, { path: "articleDetails" },
        React.createElement(ArticleCardEnergyLabel, { closeLabel: "Close dialog", icon: function () { return energyIcon; }, label: energyLabelInfo && React.createElement(Translate, { message: "energy.labelSheetTitle" }), onOpen: handleOnOpen }, function (_a) {
            var closeDialog = _a.closeDialog;
            return (React.createElement(EnergyDialog, { energyLabelInfo: energyLabelInfo, handleCloseDialog: closeDialog, configSku: sku, energyEfficiencyClass: energyEfficiencyClass }));
        })));
};
var RegularPrice = function (_a) {
    var regularPrice = _a.regularPrice, withPrimaryColor = _a.withPrimaryColor;
    return (React.createElement(TranslateScope, { path: "article" },
        React.createElement(Text, { color: "inherit", preset: Preset.XS },
            React.createElement(Translate, { message: "regularPriceShort", raw: true })),
        withPrimaryColor ? ' ' : React.createElement("br", null),
        React.createElement("span", { style: { textDecoration: 'line-through' } },
            React.createElement(FormatPrice, { price: regularPrice })),
        React.createElement(Text, { color: "inherit", preset: Preset.SUPERSCRIPT, style: { top: '-0.6em' }, tag: Tag.SUP }, "*")));
};
export var ArticleCompactCard = function (_a) {
    var brand = _a.brand, campaignIdentifier = _a.campaignIdentifier, catalogType = _a.catalogType, handleOnClick = _a.handleOnClick, media = _a.media, nameCategoryTag = _a.nameCategoryTag, nameColor = _a.nameColor, nameShop = _a.nameShop, navigationSource = _a.navigationSource, price = _a.price, sku = _a.sku, specialPrice = _a.specialPrice, _b = _a.withPrimaryColor, withPrimaryColor = _b === void 0 ? true : _b, recommendedBy = _a.recommendedBy, sustainable = _a.sustainable, energyEfficiencyClass = _a.energyEfficiencyClass, energyLabelInfo = _a.energyLabelInfo, stockStatus = _a.stockStatus, plusBenefit = _a.plusBenefit, themeHash = _a.themeHash;
    var shouldShowSustainabilityFlag = useSustainabilityFlag();
    var shouldShowPlusExclusiveElements = usePlusExclusiveElements({ plusBenefit: plusBenefit });
    var shouldShowPlusEarlyAccessElements = usePlusEarlyAccessElements({ plusBenefit: plusBenefit });
    var _c = useState(false), showReservedPopover = _c[0], toggleReservedPopover = _c[1];
    var onReservedPopoverClick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleReservedPopover(false);
    };
    var onStockStatusFlagClick = function (e) {
        e.preventDefault();
        toggleReservedPopover(true);
    };
    var priceElement = (
    // TODO: A11Y - The "1" needs visually hidden description.
    React.createElement(React.Fragment, null,
        React.createElement(FormatPrice, { price: specialPrice }),
        React.createElement(Text, { color: "inherit", preset: Preset.SUPERSCRIPT, style: { top: '-0.6em' }, tag: Tag.SUP },
            ' ',
            "1")));
    var flags = useMemo(function () {
        var allFlags = [];
        if (sustainable && shouldShowSustainabilityFlag) {
            allFlags.push(React.createElement(Flag, { key: "1", flagStyle: FlagType.SUCCESS },
                React.createElement(Translate, { message: "sustainability" })));
        }
        return allFlags.length > 0 ? allFlags : null;
    }, [shouldShowSustainabilityFlag, sustainable]);
    var shouldShowStockStatus = !!stockStatus && [StockStatus.RESERVED, StockStatus.SOLD_OUT].includes(stockStatus);
    var getStockStatusText = function (status) {
        var isReserved = status === StockStatus.RESERVED;
        return (React.createElement(TranslateScope, { path: "catalog.article" },
            React.createElement(Translate, { message: isReserved ? 'reserved' : 'sold_out', raw: true })));
    };
    var articleOverlayProps = __assign({}, (shouldShowStockStatus
        ? {
            stockStatus: stockStatus,
            getStockStatusText: getStockStatusText,
            onStockStatusFlagClick: onStockStatusFlagClick,
        }
        : {
            stockStatus: undefined,
        }));
    return (React.createElement(TranslateScope, { path: "articleDetails" },
        React.createElement(BaseArticleCard, { campaignIdentifier: campaignIdentifier, catalogType: catalogType, handleOnClick: handleOnClick, media: media, navigationSource: navigationSource, sku: sku, variant: ArticleCardVariant.COMPACT, recommendedBy: recommendedBy, themeHash: themeHash },
            React.createElement(S.StyledArticleCardContent, { prependSlot: React.createElement(React.Fragment, null,
                    shouldShowPlusExclusiveElements && React.createElement(PlusExclusiveBadge, null),
                    shouldShowPlusEarlyAccessElements && React.createElement(PlusEarlyAccessBadge, null)), brand: brand, color: nameColor, name: nameCategoryTag, energyLabelSlot: React.createElement(EnergyLabel, { energyLabelInfo: energyLabelInfo, sku: sku, energyEfficiencyClass: energyEfficiencyClass }), price: withPrimaryColor ? (React.createElement(Text, { bold: true, color: BrandColor.primary, preset: Preset.S }, priceElement)) : (priceElement), sizesSlot: withPrimaryColor ? (React.createElement(S.TextEllipsis, { preset: Preset.S }, nameCategoryTag)) : undefined, regularPrice: React.createElement(RegularPrice, { regularPrice: price, withPrimaryColor: withPrimaryColor }), shop: nameShop }),
            React.createElement(TranslateScope, { path: "catalog" },
                React.createElement(ArticleCardOverlay, __assign({ flagsLabel: "", flags: flags }, articleOverlayProps)))),
        showReservedPopover && React.createElement(ReservedPopover, { handleClick: onReservedPopoverClick })));
};
